import { useTranslation } from 'react-i18next'
import { ArrowLeft, ExternalLink, Trash2, UserX } from 'lucide-react'
import { SocialPlatform, SocialPostEngagement, SocialPostItem } from '@digital-twins/shared-types'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import { formatSocialCount, formatSocialDate, SOCIAL_PLATFORM_LABELS } from './socialFormat'

interface SocialPostDetailProps {
  post: SocialPostItem
  canManage: boolean
  onBack: () => void
  onRemovePost: (post: SocialPostItem) => void
  onRemoveAuthor: (handle: string) => void
}

const ENGAGEMENT_KEYS: Array<keyof SocialPostEngagement> = ['likes', 'comments', 'shares', 'views']

export const EMBED_SIZE_CLASS: Record<SocialPlatform, string> = {
  reddit: 'h-[480px]',
  youtube: 'aspect-video',
  tiktok: 'h-[740px] max-w-[340px]',
  instagram: 'h-[720px] max-w-[540px]',
}

export function SocialPostDetail(props: SocialPostDetailProps) {
  const { post, canManage, onBack, onRemovePost, onRemoveAuthor } = props
  const { t, i18n } = useTranslation('app')

  const author = post.authorHandle
    ? `@${post.authorHandle}`
    : t('socialListening.detail.unknownAuthor')
  const date = formatSocialDate(post.publishedAt ?? post.collectedAt, i18n.language)
  const engagement = ENGAGEMENT_KEYS.flatMap((key) => {
    const value = post.engagement[key]
    if (value == null) {
      return []
    }
    return [
      t(`socialListening.detail.engagement.${key}`, {
        count: value,
        value: formatSocialCount(value, i18n.language),
      }),
    ]
  })

  return (
    <article className="space-y-5" data-testid={`social-post-detail-${post.token}`}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="-ml-2 h-8 px-2 text-[13px] text-muted-foreground"
        data-testid="social-post-detail-back"
        onClick={onBack}
      >
        <ArrowLeft className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
        {t('socialListening.detail.backToPosts')}
      </Button>

      <header className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
          <SocialPlatformIcon platform={post.platform} className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <div className="truncate text-[15px] font-medium text-foreground">{author}</div>
          <div className="text-[12px] text-muted-foreground">
            {SOCIAL_PLATFORM_LABELS[post.platform]}
            {date ? ` · ${date}` : ''}
          </div>
        </div>
      </header>

      {post.embedUrl ? (
        <iframe
          src={post.embedUrl}
          title={t('socialListening.detail.previewTitle', { author })}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          referrerPolicy="strict-origin-when-cross-origin"
          className={`mx-auto block w-full rounded-xl border border-border bg-background ${EMBED_SIZE_CLASS[post.platform]}`}
          data-testid={`social-post-embed-${post.token}`}
        />
      ) : null}

      <Text size="sm" className="whitespace-pre-line text-[14px] leading-relaxed">
        {post.text}
      </Text>

      {engagement.length > 0 ? (
        <Text as="p" size="xs" color="muted">
          {engagement.join(' · ')}
        </Text>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
        <Button asChild variant="outline" size="sm" shape="pill" className="h-9 px-4">
          <a
            href={post.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`social-post-link-${post.token}`}
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t('socialListening.detail.openOriginal')}
          </a>
        </Button>
        {canManage ? (
          <div className="ml-auto flex items-center gap-1">
            {post.authorHandle ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-[12px] text-muted-foreground"
                data-testid={`social-post-remove-author-${post.token}`}
                onClick={() => onRemoveAuthor(post.authorHandle as string)}
              >
                <UserX className="mr-1 h-3.5 w-3.5" />
                {t('socialListening.detail.removeAuthor')}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-[12px] text-destructive hover:text-destructive"
              data-testid={`social-post-remove-${post.token}`}
              onClick={() => onRemovePost(post)}
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              {t('socialListening.detail.removePost')}
            </Button>
          </div>
        ) : null}
      </div>
    </article>
  )
}
